import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api";
import { hideLoader, showLoader } from "./loaderSlice";

export const getPetsAllReportData = createAsyncThunk(
  "getPetsAllReportData",
  async (data = { type: "pets" }, thunkAPI) => {
    try {
      // thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/pets-data`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch pets data");
    }
  },
);

export const getPetsReportData = createAsyncThunk(
  "getPetsReportData",
  async (data = { type: "pets" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());

      const params = {};
      if (data?.startDate) params.startDate = data.startDate;
      if (data?.endDate) params.endDate = data.endDate;
      if (data?.petType && data.petType !== "all") params.petType = data.petType;
      if (data?.species && data.species !== "all") params.petType = data.species;

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ auth: true }).get(`/reports/pets/overview`, { params }),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/pets-data`,
          data,
        ),
      ]);

      const overviewData = overviewRes.status === "fulfilled" ? overviewRes.value?.data || {} : {};
      const tableData = tableRes.status === "fulfilled" ? tableRes.value?.data || {} : {};

      thunkAPI.dispatch(hideLoader());

      const tableRows = Array.isArray(tableData?.data)
        ? tableData.data
        : Array.isArray(tableData)
        ? tableData
        : [];

      return {
        overview: overviewData?.data || overviewData,
        ...tableData,
        data: tableRows,
        totalRecords:
          tableData?.totalRecords ??
          tableData?.totalCount ??
          tableRows.length,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch pets data");
    }
  },
);

export const getPetsOverviewReportData = createAsyncThunk(
  "getPetsOverviewReportData",
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const isStringParam = typeof data === "string";
      const url = isStringParam ? `/reports/pets/counts${data}` : `/reports/pets/counts`;
      const config = !isStringParam && data && typeof data === "object" ? { params: data } : {};
      const res = await api({ auth: true }).get(url, config);
      if (res.status === 200 || res?.status === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch pets overview data");
    }
  },
);

export const getPetsAllAppointmentsReportData = createAsyncThunk(
  "getPetsAllAppointmentsReportData",
  async (data = { type: "appointments" }, thunkAPI) => {
    try {
      // thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/pets-data`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch pets data");
    }
  },
);

export const getPetsAppointmentsReportData = createAsyncThunk(
  "getPetsAppointmentsReportData",
  async (data = { type: "appointments" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());

      const params = {};
      if (data?.period) params.period = data.period;
      if (data?.startDate) params.startDate = data.startDate;
      if (data?.endDate) params.endDate = data.endDate;

      if (data?.petName) {
        params.petName = Array.isArray(data.petName) ? data.petName[0] : data.petName;
      }
      if (data?.vetName) {
        params.vetName = Array.isArray(data.vetName) ? data.vetName[0] : data.vetName;
      }
      if (data?.serviceType) {
        params.serviceType = Array.isArray(data.serviceType) ? data.serviceType[0] : data.serviceType;
      }
      params.topN = data?.topN || 5;

      const [historyRes, tableRes] = await Promise.allSettled([
        api({ auth: true }).get(`/reports/pets/appointment-history`, { params }),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/pets-data`,
          data,
        ),
      ]);

      const historyData = historyRes.status === "fulfilled" ? historyRes.value?.data || {} : {};
      const tableData = tableRes.status === "fulfilled" ? tableRes.value?.data || {} : {};

      thunkAPI.dispatch(hideLoader());

      const tableRows = Array.isArray(tableData?.data)
        ? tableData.data
        : Array.isArray(tableData)
        ? tableData
        : [];

      return {
        appointmentHistory: historyData?.data || historyData,
        ...tableData,
        data: tableRows,
        totalRecords:
          tableData?.totalRecords ??
          tableData?.totalCount ??
          tableRows.length,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch pets appointment history data");
    }
  },
);

export const getVetsRevenueReportData = createAsyncThunk(
  "getVetsRevenueReportData",
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const isStringParam = typeof data === "string";
      const url = isStringParam ? `/reports/performance${data}` : `/reports/performance`;
      const config = !isStringParam && data && typeof data === "object" ? { params: data } : {};

      const postBody =
        typeof data === "object" && data !== null
          ? { type: "revenue", ...data }
          : { type: "revenue" };

      const [perfRes, tableRes] = await Promise.allSettled([
        api({ auth: true }).get(url, config),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/consultations`,
          postBody,
        ),
      ]);

      const perfData = perfRes.status === "fulfilled" ? perfRes.value?.data || {} : {};
      const tableResData = tableRes.status === "fulfilled" ? tableRes.value?.data || {} : {};

      thunkAPI.dispatch(hideLoader());

      const tableRows =
        Array.isArray(tableResData?.data)
          ? tableResData.data
          : Array.isArray(tableResData)
          ? tableResData
          : [];

      return {
        ...perfData,
        tableData: tableRows,
        totalRecords:
          tableResData?.totalRecords ??
          (tableResData?.totalCount || tableRows.length),
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch vet performance data");
    }
  },
);

export const getVetsPerformanceByVetReportData = createAsyncThunk(
  "getVetsPerformanceByVetReportData",
  async (data, thunkAPI) => {
    try {
      const isStringParam = typeof data === "string";
      const url = isStringParam
        ? `/reports/performance/consultations/by-vet${data}`
        : `/reports/performance/consultations/by-vet`;
      const config = !isStringParam && data && typeof data === "object" ? { params: data } : {};
      const res = await api({ auth: true }).get(url, config);
      if (res.status === 200 || res?.status === 201) {
        return res.data;
      }
      return {};
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch vet performance by vet data");
    }
  },
);

export const getVetsPerformanceWeeklyReportData = createAsyncThunk(
  "getVetsPerformanceWeeklyReportData",
  async (data, thunkAPI) => {
    try {
      const isStringParam = typeof data === "string";
      const url = isStringParam
        ? `/reports/performance/consultations/weekly${data}`
        : `/reports/performance/consultations/weekly`;
      const config = !isStringParam && data && typeof data === "object" ? { params: data } : {};
      const res = await api({ auth: true }).get(url, config);
      if (res.status === 200 || res?.status === 201) {
        return res.data;
      }
      return {};
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch vet performance weekly data");
    }
  },
);

export const getVetsRevenueAllReportData = createAsyncThunk(
  "getVetsRevenueAllReportData",
  async (data = { type: "revenue" }, thunkAPI) => {
    try {
      // thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/consultations`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch vet revenue data");
    }
  },
);

export const getVetsSpecialityReportData = createAsyncThunk(
  "getVetsSpecialityReportData",
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const isStringParam = typeof data === "string";
      const url = isStringParam ? `/reports/vet-specialties${data}` : `/reports/vet-specialties`;
      const config = !isStringParam && data && typeof data === "object" ? { params: data } : {};
      const res = await api({ auth: true }).get(url, config);
      if (res.status === 200 || res?.status === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch vet speciality data");
    }
  },
);

export const getVetsSlotsReportData = createAsyncThunk(
  "getVetsSlotsReportData",
  async (data, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const isStringParam = typeof data === "string";
      const url = isStringParam
        ? `/reports/utilization-capacity${data}`
        : `/reports/utilization-capacity`;
      const config =
        !isStringParam && data && typeof data === "object" ? { params: data } : {};
      const res = await api({ auth: true }).get(url, config);
      if (res.status === 200 || res?.status === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch vet slots data");
    }
  },
);

export const getVetsDelaysReportData = createAsyncThunk(
  "getVetsDelaysReportData",
  async (url = "?type=delay", thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/dashboard/consultations${url}`,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch vet delay data");
    }
  },
);

export const getVetsUpcomingsReportData = createAsyncThunk(
  "getVetsUpcomingsReportData",
  async (data = { type: "upcoming" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/consultations`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch vet upcoming data");
    }
  },
);

export const getAppointmentsTableReportData = createAsyncThunk(
  "getAppointmentsTableReportData",
  async (data = { type: "appointments", itemSize: 10, page: 1 }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());

      const params = {};
      if (data?.granularity || data?.Granularity) {
        params.granularity = data.granularity || data.Granularity;
      }
      if (data?.startDate) params.startDate = data.startDate;
      if (data?.endDate) params.endDate = data.endDate;

      const [trendsRes, tableRes] = await Promise.allSettled([
        api({ auth: true }).get(`/reports/appointments/trends`, { params }),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/appointments/comprehensive`,
          data,
        ),
      ]);

      const trendsData = trendsRes.status === "fulfilled" ? trendsRes.value?.data || {} : {};
      const tableData = tableRes.status === "fulfilled" ? tableRes.value?.data || {} : {};

      thunkAPI.dispatch(hideLoader());

      const tableRows = Array.isArray(tableData?.data)
        ? tableData.data
        : Array.isArray(tableData)
        ? tableData
        : [];

      return {
        appointmentTrends: trendsData?.data || trendsData,
        ...tableData,
        data: tableRows,
        totalRecords:
          tableData?.totalRecords ??
          tableData?.totalCount ??
          tableRows.length,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch appointment trends data");
    }
  },
);

export const getAppointmentAnalyticsReportData = createAsyncThunk(
  "getAppointmentAnalyticsReportData",
  async (data = {}, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.startDate) queryParams.append("startDate", data.startDate);
      if (data?.endDate) queryParams.append("endDate", data.endDate);
      if (data?.consultationMode) queryParams.append("consultationMode", data.consultationMode);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
      const res = await api({ contentType: true, auth: true }).get(
        `/reports/appointments/analytics${queryString}`,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch appointment analytics data");
    }
  },
);

export const getAppointmentsFacilityReportData = createAsyncThunk(
  "getAppointmentsFacilityReportData",
  async (data = { type: "facility", page: 1, itemSize: 10 }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.startDate) queryParams.append("startDate", data.startDate);
      if (data?.endDate) queryParams.append("endDate", data.endDate);
      if (data?.period) queryParams.append("period", data.period);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [utilRes, compRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(
          `/reports/facilities/utilization${queryString}`,
        ),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/appointments/comprehensive`,
          data,
        ),
      ]);

      const utilizationData =
        utilRes.status === "fulfilled" && (utilRes.value?.status === 200 || utilRes.value?.payload === 201)
          ? utilRes.value.data?.data || utilRes.value.data
          : {};

      const compData =
        compRes.status === "fulfilled" && (compRes.value?.status === 200 || compRes.value?.payload === 201)
          ? compRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...utilizationData,
        utilizationData,
        compData,
        data: utilizationData?.facilities || compData?.data || [],
        totalRecords:
          utilizationData?.facilities?.length ??
          compData?.totalRecords ??
          compData?.totalCount ??
          0,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch facility data");
    }
  },
);

export const getAllBillingRevenueReportData = createAsyncThunk(
  "getAllBillingRevenueReportData",
  async (data = {}, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/billing/revenue-dump`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch billing revenue data");
    }
  },
);

export const getBillingRevenueReportData = createAsyncThunk(
  "getBillingRevenueReportData",
  async (data = { page: 1, itemSize: 10 }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.startDate) queryParams.append("startDate", data.startDate);
      if (data?.endDate) queryParams.append("endDate", data.endDate);
      if (data?.period) queryParams.append("period", data.period);
      if (data?.topN) queryParams.append("topN", data.topN ?? 10);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/revenue${queryString}`),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/billing/revenue-dump`,
          data,
        ),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || [],
        totalRecords: tableData?.totalRecords ?? tableData?.totalCount ?? 0,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch billing revenue data");
    }
  },
);
export const getBillingCollectionsReportData = createAsyncThunk(
  "getBillingCollectionsReportData",
  async (data = { page: 1, itemSize: 10 }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.startDate) queryParams.append("startDate", data.startDate);
      if (data?.endDate) queryParams.append("endDate", data.endDate);
      if (data?.paymentMode && data.paymentMode !== "all") queryParams.append("paymentMode", data.paymentMode);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/collections${queryString}`),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/billing/collections-dump`,
          data,
        ),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || [],
        totalRecords: tableData?.totalRecords ?? tableData?.totalCount ?? 0,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch billing collections data");
    }
  },
);

export const getBillingReturnsReportData = createAsyncThunk(
  "getBillingReturnsReportData",
  async (data = { type: "Sales" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.startDate) queryParams.append("startDate", data.startDate);
      if (data?.endDate) queryParams.append("endDate", data.endDate);
      if (data?.type) queryParams.append("type", data.type);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/returns${queryString}`),
        api({ contentType: true, auth: true }).get(
          `/clinic/supplier?purchaseType=${data?.type || "Sales"}`,
        ),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || tableData || [],
        totalRecords:
          tableData?.totalRecords ??
          tableData?.totalCount ??
          (Array.isArray(tableData) ? tableData.length : 0),
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch billing returns data");
    }
  },
);

export const getBillingSalesReturnReportData = createAsyncThunk(
  "getBillingSalesReturnReportData",
  async (url = "?purchaseType=Sales", thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/dashboard/billing/revenue-dump${url}`,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch billing return data");
    }
  },
);

export const getBillingPurchaseReturnReportData = createAsyncThunk(
  "getBillingPurchaseReturnReportData",
  async (url = "?purchaseType=Purchase", thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/dashboard/billing/revenue-dump${url}`,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch billing return data");
    }
  },
);

export const getAllStockDumpReportData = createAsyncThunk(
  "getAllStockDumpReportData",
  async (data = { type: "dump" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/inventory/stock`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch stock dump data");
    }
  },
);

export const getStockDumpReportData = createAsyncThunk(
  "getStockDumpReportData",
  async (data = { type: "dump" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.warehouse && data.warehouse !== "all") queryParams.append("warehouse", data.warehouse);
      if (data?.category && data.category !== "all") queryParams.append("category", data.category);
      if (data?.period) queryParams.append("period", data.period);
      if (data?.locationId) queryParams.append("locationId", data.locationId);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/stocks/overview${queryString}`),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/inventory/stock`,
          data,
        ),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || [],
        totalRecords: tableData?.totalRecords ?? tableData?.totalCount ?? 0,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch stock dump data");
    }
  },
);

export const getAllStockBatchReportData = createAsyncThunk(
  "getAllStockBatchReportData",
  async (data = { type: "batch" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/inventory/stock`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch stock batch data");
    }
  },
);

export const getStockBatchReportData = createAsyncThunk(
  "getStockBatchReportData",
  async (data = { type: "batch" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.batchNo && data.batchNo !== "all") queryParams.append("batchNo", data.batchNo);
      if (data?.locationId) queryParams.append("locationId", data.locationId);
      if (data?.category && data.category !== "all") queryParams.append("category", data.category);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/stocks/batches${queryString}`),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/inventory/stock`,
          data,
        ),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || [],
        totalRecords: tableData?.totalRecords ?? tableData?.totalCount ?? 0,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch stock batch data");
    }
  },
);

export const getStockSalesReturnReportData = createAsyncThunk(
  "getStockSalesReturnReportData",
  async (data = {}, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.startDate) queryParams.append("startDate", data.startDate);
      if (data?.endDate) queryParams.append("endDate", data.endDate);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/stocks/returns/sales${queryString}`),
        api({ contentType: true, auth: true }).get(`/appointment/clinic/payments?filter=credit&type=credit`),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || tableData || [],
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch stock sales return data");
    }
  },
);

export const getStockPurchaseReturnReportData = createAsyncThunk(
  "getStockPurchaseReturnReportData",
  async (data = {}, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.startDate) queryParams.append("startDate", data.startDate);
      if (data?.endDate) queryParams.append("endDate", data.endDate);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/stocks/returns/purchase${queryString}`),
        api({ contentType: true, auth: true }).get(`/clinic/supplier?filter=debit&type=debit`),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || tableData || [],
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch stock purchase return data");
    }
  },
);

export const getStockReturnReportData = createAsyncThunk(
  "getStockReturnReportData",
  async (url = "?type=returns", thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/dashboard/inventory/stock${url}`,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch stock return data");
    }
  },
);

export const getAllServiceTableReportData = createAsyncThunk(
  "getAllServiceTableReportData",
  async (data = { type: "summary" }, thunkAPI) => {
    try {
      // thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/services/table-data`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        // thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      // thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      // thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch service report data");
    }
  },
);

export const getServiceTableReportData = createAsyncThunk(
  "getServiceTableReportData",
  async (data = { type: "summary", page: 1, itemSize: 10 }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.category && data.category !== "all") queryParams.append("category", data.category);
      if (data?.period) queryParams.append("period", data.period);
      if (data?.medicalFilter) queryParams.append("medicalFilter", data.medicalFilter);
      if (data?.durationFilter) queryParams.append("durationFilter", data.durationFilter);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/services${queryString}`),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/services/table-data`,
          data,
        ),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || [],
        totalRecords: tableData?.totalRecords ?? tableData?.totalCount ?? 0,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch service report data");
    }
  },
);

export const getAllServiceAppointmentsReportData = createAsyncThunk(
  "getAllServiceAppointmentsReportData",
  async (data = { type: "detail" }, thunkAPI) => {
    try {
      // thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/clinic/dashboard/services/table-data`,
        data,
      );
      if (res.status === 200 || res?.payload === 201) {
        thunkAPI.dispatch(hideLoader());
        return res.data;
      }
      thunkAPI.dispatch(hideLoader());
      return {};
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch service report data");
    }
  },
);

export const getServiceAppointmentsReportData = createAsyncThunk(
  "getServiceAppointmentsReportData",
  async (data = { type: "detail" }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const queryParams = new URLSearchParams();
      if (data?.startDate) queryParams.append("startDate", data.startDate);
      if (data?.endDate) queryParams.append("endDate", data.endDate);
      if (data?.period) queryParams.append("period", data.period);
      if (data?.vetName) queryParams.append("vetName", data.vetName);
      if (data?.petName) queryParams.append("petName", data.petName);
      if (data?.category && data.category !== "all") queryParams.append("category", data.category);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const [overviewRes, tableRes] = await Promise.allSettled([
        api({ contentType: true, auth: true }).get(`/reports/services/usage${queryString}`),
        api({ contentType: true, auth: true }).post(
          `/clinic/dashboard/services/table-data`,
          data,
        ),
      ]);

      const overviewData =
        overviewRes.status === "fulfilled" && (overviewRes.value?.status === 200 || overviewRes.value?.payload === 201)
          ? overviewRes.value.data?.data || overviewRes.value.data
          : {};

      const tableData =
        tableRes.status === "fulfilled" && (tableRes.value?.status === 200 || tableRes.value?.payload === 201)
          ? tableRes.value.data
          : {};

      thunkAPI.dispatch(hideLoader());
      return {
        ...tableData,
        overview: overviewData,
        tableData,
        data: tableData?.data || [],
        totalRecords: tableData?.totalRecords ?? tableData?.totalCount ?? 0,
      };
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch service report data");
    }
  },
);

export const getSalesReturnReportData = createAsyncThunk(
  "getSalesReturnReportData",
  async (url = "", thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/appointment/clinic/payments${url}`,
      );
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data;
      } else if (res.status !== 200) {
        thunkAPI.dispatch(hideLoader());
      }
      thunkAPI.dispatch(hideLoader());
      return null;
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
    }
  },
);

export const getPurchaseReturnReportData = createAsyncThunk(
  "getPurchaseReturnReportData",
  async (url = "", thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/supplier${url}`,
      );
      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data;
      } else if (res.status !== 200) {
        thunkAPI.dispatch(hideLoader());
      }
      thunkAPI.dispatch(hideLoader());
      return null;
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
    }
  },
);

const reportSlice = createSlice({
  name: "report",
  initialState: {
    allPets: {},
    pets: {},
    petsOverview: {},
    allAppointments: {},
    appointments: {},
    vetRevenue: {},
    vetRevenueAll: {},
    vetPerformanceByVet: {},
    vetPerformanceWeekly: {},
    vetSpeciality: {},
    vetSlots: {},
    vetDelays: {},
    vetUpcomings: {},
    appointmentTable: {},
    appoinmentFacility: {},
    appointmentAnalytics: {},
    billingRevenueAll: {},
    billingRevenue: {},
    billingSalesReturn: {},
    billingPurchaseReturn: {},
    allServiceTable: {},
    serviceTable: {},
    allServiceAppointments: {},
    serviceAppointments: {},
    stockDumpAll: {},
    stockDump: {},
    stockBatchAll: {},
    stockBatch: {},
    stockReturn: {},
    stockSalesReturn: {},
    stockPurchaseReturn: {},
    salesReturn: {},
    purchaseReturn: {},
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getPetsAllReportData.fulfilled, (state, action) => {
      state.allPets = action.payload ?? {};
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getPetsReportData.fulfilled, (state, action) => {
      state.pets = action.payload ?? {};
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getPetsOverviewReportData.fulfilled, (state, action) => {
      state.petsOverview = action.payload ?? {};
      state.loading = false;
      state.error = null;
    });
    builder.addCase(
      getPetsAllAppointmentsReportData.fulfilled,
      (state, action) => {
        state.allAppointments = action.payload ?? {};
        state.loading = false;
        state.error = null;
      },
    );
    builder.addCase(
      getPetsAppointmentsReportData.fulfilled,
      (state, action) => {
        state.appointments = action.payload ?? {};
        state.loading = false;
        state.error = null;
      },
    );
    builder.addCase(getVetsRevenueReportData.fulfilled, (state, action) => {
      state.vetRevenue = action.payload ?? {};
    });
    builder.addCase(getVetsRevenueAllReportData.fulfilled, (state, action) => {
      state.vetRevenueAll = action.payload ?? {};
    });
    builder.addCase(
      getVetsPerformanceByVetReportData.fulfilled,
      (state, action) => {
        state.vetPerformanceByVet = action.payload ?? {};
      },
    );
    builder.addCase(
      getVetsPerformanceWeeklyReportData.fulfilled,
      (state, action) => {
        state.vetPerformanceWeekly = action.payload ?? {};
      },
    );
    builder.addCase(getVetsSpecialityReportData.fulfilled, (state, action) => {
      state.vetSpeciality = action.payload ?? {};
    });
    builder.addCase(getVetsSlotsReportData.fulfilled, (state, action) => {
      state.vetSlots = action.payload ?? {};
    });
    builder.addCase(getVetsDelaysReportData.fulfilled, (state, action) => {
      state.vetDelays = action.payload ?? {};
    });
    builder.addCase(getVetsUpcomingsReportData.fulfilled, (state, action) => {
      state.vetUpcomings = action.payload ?? {};
    });
    builder.addCase(
      getAppointmentsTableReportData.fulfilled,
      (state, action) => {
        state.appointmentTable = action.payload ?? {};
      },
    );
    builder.addCase(
      getAppointmentsFacilityReportData.fulfilled,
      (state, action) => {
        state.appoinmentFacility = action.payload ?? {};
      },
    );
    builder.addCase(
      getAppointmentAnalyticsReportData.fulfilled,
      (state, action) => {
        state.appointmentAnalytics = action.payload ?? {};
      },
    );
    builder.addCase(
      getAllBillingRevenueReportData.fulfilled,
      (state, action) => {
        state.billingRevenueAll = action.payload ?? {};
      },
    );
    builder.addCase(getBillingRevenueReportData.fulfilled, (state, action) => {
      state.billingRevenue = action.payload ?? {};
    });
    builder.addCase(
      getBillingCollectionsReportData.fulfilled,
      (state, action) => {
        state.billingCollections = action.payload ?? {};
      },
    );
    builder.addCase(
      getBillingReturnsReportData.fulfilled,
      (state, action) => {
        state.billingReturns = action.payload ?? {};
      },
    );
    builder.addCase(
      getBillingSalesReturnReportData.fulfilled,
      (state, action) => {
        state.billingSalesReturn = action.payload ?? {};
      },
    );
    builder.addCase(
      getBillingPurchaseReturnReportData.fulfilled,
      (state, action) => {
        state.billingPurchaseReturn = action.payload ?? {};
      },
    );
    builder.addCase(getAllStockDumpReportData.fulfilled, (state, action) => {
      state.stockDumpAll = action.payload ?? {};
    });
    builder.addCase(getStockDumpReportData.fulfilled, (state, action) => {
      state.stockDump = action.payload ?? {};
    });
    builder.addCase(getAllStockBatchReportData.fulfilled, (state, action) => {
      state.stockBatchAll = action.payload ?? {};
    });
    builder.addCase(getStockBatchReportData.fulfilled, (state, action) => {
      state.stockBatch = action.payload ?? {};
    });
    builder.addCase(getStockReturnReportData.fulfilled, (state, action) => {
      state.stockReturn = action.payload ?? {};
    });
    builder.addCase(getStockSalesReturnReportData.fulfilled, (state, action) => {
      state.stockSalesReturn = action.payload ?? {};
    });
    builder.addCase(getStockPurchaseReturnReportData.fulfilled, (state, action) => {
      state.stockPurchaseReturn = action.payload ?? {};
    });
    builder.addCase(getAllServiceTableReportData.fulfilled, (state, action) => {
      state.allServiceTable = action.payload ?? {};
    });
    builder.addCase(getServiceTableReportData.fulfilled, (state, action) => {
      state.serviceTable = action.payload ?? {};
    });
    builder.addCase(
      getAllServiceAppointmentsReportData.fulfilled,
      (state, action) => {
        state.allServiceAppointments = action.payload ?? {};
      },
    );
    builder.addCase(
      getServiceAppointmentsReportData.fulfilled,
      (state, action) => {
        state.serviceAppointments = action.payload ?? {};
      },
    );
    builder.addCase(getSalesReturnReportData.fulfilled, (state, action) => {
      state.salesReturn = action.payload ?? {};
    });
    builder.addCase(getPurchaseReturnReportData.fulfilled, (state, action) => {
      state.purchaseReturn = action.payload ?? {};
    });
  },
});

export default reportSlice.reducer;
